import React, { useState } from 'react';
import { FiPlus, FiTrash2, FiPlay, FiSquare, FiUpload } from 'react-icons/fi';
import { adminAPI } from '../../../../services/api';
import ttsService from '../../../../utils/textToSpeech';

const SentencesBlockEditor = ({ block, onUpdate, availableVoices = [], setError, setSuccess }) => {
    const [sentences, setSentences] = useState(block.sentences || []);
    const [isPlayingIndex, setIsPlayingIndex] = useState(null);

    const handleSentencesChange = (updatedSentences) => {
        setSentences(updatedSentences);
        onUpdate({ sentences: updatedSentences });
    };

    const addSentence = () => {
        const updated = [...sentences, {
            en: '',
            ru: '',
            parts: [],
            partsRu: [],
            voiceId: 'en-US-JennyNeural',
            audioType: 'auto',
            audioUrl: ''
        }];
        handleSentencesChange(updated);
    };

    const updateSentence = (index, field, value) => {
        const updated = [...sentences];
        updated[index][field] = value;

        // Автоматически определяем части из разделителей (независимо для каждого языка)
        if (field === 'en') {
            const enParts = updated[index].en.split('|').map(p => p.trim()).filter(p => p);
            if (enParts.length > 1) {
                updated[index].parts = enParts;
            } else {
                updated[index].parts = [];
            }
        }
        
        if (field === 'ru') {
            const ruParts = updated[index].ru.split('|').map(p => p.trim()).filter(p => p);
            if (ruParts.length > 1) {
                updated[index].partsRu = ruParts;
            } else {
                updated[index].partsRu = [];
            }
        }

        handleSentencesChange(updated);
    };

    const removeSentence = (index) => {
        handleSentencesChange(sentences.filter((_, i) => i !== index));
    };

    const playSentence = (text, voiceId, index) => {
        if (!text) {
            alert('Введите текст предложения');
            return;
        }

        const cleanText = text.replace(/\s*\|\s*/g, ' ').trim();
        setIsPlayingIndex(index);

        ttsService.speak(cleanText, {
            rate: 1,
            lang: 'en-US',
            voice: voiceId || 'en-US-JennyNeural'
        }).then(() => {
            setIsPlayingIndex(null);
        }).catch(error => {
            console.error('Ошибка воспроизведения:', error);
            setError('Ошибка воспроизведения');
            setIsPlayingIndex(null);
        });
    };

    const stopAudio = () => {
        ttsService.cancel();
        setIsPlayingIndex(null);
    };

    const handleAudioUpload = async (index, file) => {
        if (!file || !file.type.startsWith('audio/')) {
            setError('Выберите аудио файл');
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            setError('Размер файла не должен превышать 10MB');
            return;
        }

        try {
            const formData = new FormData();
            formData.append('audio', file);

            const response = await adminAPI.uploadSentenceAudio(formData);

            const updated = [...sentences];
            updated[index].audioUrl = response.data.audioUrl;
            updated[index].audioType = 'custom';
            handleSentencesChange(updated);

            setSuccess('Аудио успешно загружено');
        } catch (error) {
            console.error('Ошибка загрузки:', error);
            setError('Ошибка при загрузке аудио');
        }
    };

    return (
        <div>
            <div className="form-group" style={{ marginBottom: '20px' }}>
                <label className="form-label">Описание задания</label>
                <p style={{ fontSize: '12px', color: '#888', margin: '4px 0 8px 0' }}>
                    Пояснение для ученика под заголовком блока (как выполнять задание).
                </p>
                <textarea
                    className="form-input"
                    placeholder="Например: прослушайте каждое предложение и повторяйте вслух…"
                    value={block.description || ''}
                    onChange={(e) => onUpdate({ description: e.target.value })}
                    rows={3}
                />
            </div>

            <div className="form-group" style={{ marginBottom: '20px' }}>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        gap: '12px',
                    }}
                >
                    <input
                        type="checkbox"
                        id={`combined-speech-${block.id ?? 'new'}`}
                        checked={!!block.combinedSpeechTask}
                        onChange={(e) => onUpdate({ combinedSpeechTask: e.target.checked })}
                        style={{
                            flex: '0 0 auto',
                            width: '18px',
                            height: '18px',
                            minWidth: '18px',
                            margin: '2px 0 0 0',
                            cursor: 'pointer',
                            alignSelf: 'flex-start',
                        }}
                    />
                    <label
                        htmlFor={`combined-speech-${block.id ?? 'new'}`}
                        style={{
                            flex: '1 1 auto',
                            minWidth: 0,
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: '#333',
                            lineHeight: 1.55,
                            margin: 0,
                            padding: 0,
                            fontWeight: 'normal',
                        }}
                    >
                        <strong>Итоговое задание</strong> — внизу блока ученику показывается{' '}
                        <em>ещё одна</em> зона записи: все предложения <em>одним аудио подряд</em>. Запись по каждой
                        строке при этом остаётся как обычно.
                    </label>
                </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
                <label className="form-label">Предложения для прослушивания</label>
                <p style={{ fontSize: '13px', color: '#666', marginTop: '5px' }}>
                    Используйте символ <code>|</code> для переноса текста на новую строку
                </p>
            </div>

            {(!sentences || sentences.length === 0) ? (
                <div style={{
                    padding: '40px',
                    textAlign: 'center',
                    background: '#fafafa',
                    borderRadius: '8px',
                    border: '1px dashed #d9d9d9',
                    color: '#999'
                }}>
                    Нет предложений. Нажмите "Добавить предложение" чтобы начать.
                </div>
            ) : (
                sentences.map((sentence, index) => (
                <div
                    key={index}
                    style={{
                        padding: '15px',
                        background: '#f9f9f9',
                        borderRadius: '8px',
                        marginBottom: '12px',
                        border: '1px solid #e0e0e0'
                    }}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <span style={{ fontWeight: '600', color: '#666' }}>
                            Предложение {index + 1}
                        </span>
                        <button
                            type="button"
                            onClick={() => removeSentence(index)}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: '#ff4d4f',
                                cursor: 'pointer',
                                fontSize: '18px'
                            }}
                            title="Удалить"
                        >
                            <FiTrash2 />
                        </button>
                    </div>

                    <div style={{ display: 'grid', gap: '12px' }}>
                        <div>
                            <label className="form-label" style={{ fontSize: '13px' }}>
                                Английский текст
                            </label>
                            <textarea
                                className="form-input"
                                placeholder="I am going to the store with my friend"
                                value={sentence.en}
                                onChange={(e) => updateSentence(index, 'en', e.target.value)}
                                rows={2}
                            />
                        </div>

                        <div>
                            <label className="form-label" style={{ fontSize: '13px' }}>
                                Русский перевод
                            </label>
                            <textarea
                                className="form-input"
                                placeholder="Я иду в магазин с моим другом"
                                value={sentence.ru}
                                onChange={(e) => updateSentence(index, 'ru', e.target.value)}
                                rows={2}
                            />
                        </div>

                        <div>
                            <label className="form-label" style={{ fontSize: '13px' }}>
                                Озвучка
                            </label>
                            <div style={{ marginBottom: '12px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', cursor: 'pointer' }}>
                                    <input
                                        type="radio"
                                        name={`audio-type-${index}`}
                                        checked={sentence.audioType === 'auto'}
                                        onChange={() => updateSentence(index, 'audioType', 'auto')}
                                    />
                                    <span style={{ fontSize: '13px' }}>Автоматическая озвучка (TTS)</span>
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input
                                        type="radio"
                                        name={`audio-type-${index}`}
                                        checked={sentence.audioType === 'custom'}
                                        onChange={() => updateSentence(index, 'audioType', 'custom')}
                                    />
                                    <span style={{ fontSize: '13px' }}>Загрузить свое аудио</span>
                                </label>
                            </div>

                            {sentence.audioType === 'auto' ? (
                                <div>
                                    <label className="form-label" style={{ fontSize: '12px', marginBottom: '6px' }}>
                                        Выбор голоса
                                    </label>
                                    <select
                                        className="form-input"
                                        value={sentence.voiceId || 'en-US-JennyNeural'}
                                        onChange={(e) => updateSentence(index, 'voiceId', e.target.value)}
                                    >
                                        {availableVoices && availableVoices.length > 0 ? (
                                            availableVoices.filter(v => v.ShortName && v.DisplayName).map(voice => (
                                                <option key={voice.ShortName} value={voice.ShortName}>
                                                    {voice.DisplayName}
                                                </option>
                                            ))
                                        ) : (
                                            <option value="en-US-JennyNeural">Jenny (США, женский)</option>
                                        )}
                                    </select>
                                </div>
                            ) : (
                                <div>
                                    <input
                                        type="file"
                                        accept="audio/*"
                                        onChange={(e) => handleAudioUpload(index, e.target.files[0])}
                                        style={{ display: 'none' }}
                                        id={`audio-upload-${block.id}-${index}`}
                                    />
                                    <label
                                        htmlFor={`audio-upload-${block.id}-${index}`}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            padding: '10px 16px',
                                            background: sentence.audioUrl ? '#52c41a' : '#4a90e2',
                                            color: 'white',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '13px',
                                            fontWeight: '500'
                                        }}
                                    >
                                        <FiUpload /> {sentence.audioUrl ? '✓ Аудио загружено' : 'Загрузить аудио файл'}
                                    </label>
                                    {sentence.audioUrl && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                updateSentence(index, 'audioUrl', '');
                                            }}
                                            style={{
                                                marginLeft: '10px',
                                                padding: '10px 16px',
                                                background: 'transparent',
                                                color: '#ff4d4f',
                                                border: '1px solid #ff4d4f',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: '13px'
                                            }}
                                        >
                                            Удалить
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                            <button
                                type="button"
                                onClick={() => {
                                    if (isPlayingIndex === index) {
                                        stopAudio();
                                    } else {
                                        playSentence(sentence.en, sentence.voiceId, index);
                                    }
                                }}
                                disabled={!sentence.en}
                                style={{
                                    padding: '8px 16px',
                                    background: isPlayingIndex === index ? '#ff4d4f' : '#4a90e2',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: sentence.en ? 'pointer' : 'not-allowed',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    fontSize: '13px'
                                }}
                            >
                                {isPlayingIndex === index ? <><FiSquare /> Стоп</> : <><FiPlay /> Прослушать</>}
                            </button>

                            {(sentence.parts && sentence.parts.length > 0) || (sentence.partsRu && sentence.partsRu.length > 0) ? (
                                <div style={{
                                    padding: '8px 12px',
                                    background: '#e6f7ff',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    color: '#0050b3',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    {sentence.parts && sentence.parts.length > 0 && (
                                        <span>EN: {sentence.parts.length} {sentence.parts.length === 1 ? 'строка' : sentence.parts.length < 5 ? 'строки' : 'строк'}</span>
                                    )}
                                    {sentence.partsRu && sentence.partsRu.length > 0 && (
                                        <span>RU: {sentence.partsRu.length} {sentence.partsRu.length === 1 ? 'строка' : sentence.partsRu.length < 5 ? 'строки' : 'строк'}</span>
                                    )}
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            )))}

            <button
                type="button"
                onClick={addSentence}
                style={{
                    width: '100%',
                    padding: '12px',
                    background: 'white',
                    border: '2px dashed #d9d9d9',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    color: '#666',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px'
                }}
                onMouseEnter={(e) => {
                    e.target.style.borderColor = '#4a90e2';
                    e.target.style.color = '#4a90e2';
                }}
                onMouseLeave={(e) => {
                    e.target.style.borderColor = '#d9d9d9';
                    e.target.style.color = '#666';
                }}
            >
                <FiPlus /> Добавить предложение
            </button>
        </div>
    );
};

export default SentencesBlockEditor;